import { createApi } from '@reduxjs/toolkit/query/react';
import { FilterDTOType } from '../../schemas';
import { overrideFetchBaseQuery } from '../utils/overrideFetchBaseQuery';
import { ProductSearchResponseType } from './types';
import { baseQueryWithZodValidation } from '../utils/baseQueryWithZodValidation';
import { categoryApi } from '../category/categoryApi';

export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery: baseQueryWithZodValidation(overrideFetchBaseQuery({}), {}),
  tagTypes: [],
  endpoints: builder => ({
    searchProducts: builder.infiniteQuery<
      ProductSearchResponseType,
      {
        filters?: Array<FilterDTOType>;
      },
      {
        page: number;
        size: number;
        filters: Array<FilterDTOType>;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          page: 1,
          size: 10,
          filters: [],
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.results.length === 0) {
            return undefined;
          }

          if (lastPage.results.length < lastPageParam.size) {
            return undefined;
          }

          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
            filters: lastPageParam.filters,
          };
        },
      },
      query: ({ pageParam, queryArg }) => {
        const page = pageParam.page;
        const size = pageParam.size;
        const filters = queryArg.filters ?? [];
        return {
          url: `/product/search?page=${page}&size=${size}`,
          method: 'POST',
          body: { filters },
        };
      },
      onQueryStarted: async (_, { queryFulfilled, getCacheEntry, updateCachedData, dispatch }) => {
        await queryFulfilled;

        // * Enrich Product Type Id facets with names
        const productTypeIds = getCacheEntry().data?.pages.flatMap(p =>
          p.facets.find(f => f.key.key === 'productTypeId')?.values?.map(v => v.id),
        );

        try {
          const categoryResult = await dispatch(
            categoryApi.endpoints.searchCategories.initiate({
              query: {
                filters: [
                  {
                    field: 'id',
                    filterType: 'IN',
                    value: productTypeIds?.join(),
                  },
                ],
              },
              page: 1,
              size: 10000,
            }),
          );

          updateCachedData(draft => {
            if (categoryResult.data && draft.pages) {
              const categoryMap = new Map(
                categoryResult.data.map(category => [category.id.toString(), category.name]),
              );

              draft.pages.forEach(page => {
                const productTypeFacet = page.facets.find(f => f.key.key === 'productTypeId');

                if (productTypeFacet?.values) {
                  productTypeFacet.values.forEach(facetValue => {
                    const categoryName = categoryMap.get(facetValue.id);
                    if (categoryName) {
                      facetValue.value = categoryName;
                    }
                  });
                }
              });
            }
          });
        } catch (error) {
          console.error('Error calling category API:', error);
        }
      },
      providesTags: [],
    }),
  }),
});

export const { useSearchProductsInfiniteQuery } = productsApi;
